import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  FormFeedback,
  Icon,
  IconButton,
  Row,
  TabsFactory,
  Text,
  Title,
} from '@wyz/ui';
import { useFieldArray, useForm, UseFormGetFieldState } from 'react-hook-form';
import {
  CheckboxField,
  CriteriaBuilder,
  MatchedCustomersTable,
  TextField,
} from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import {
  AlphabeticalListSearchInput,
  BrandAlphabeticalList,
  BrandDraggables,
  DesadvGlobalCheckbox,
  SelectBrandEanConfiguration,
} from '~/connect/components';
import {
  BRAND_EAN_SPECIFICATIONS,
  brandTemplateFormSchema,
} from '~/connect/schemas';
import {
  BrandTemplateTabType,
  IBrandTemplate,
  IBrandTemplateBrand,
  IBrandTemplateForm,
  IBrandTemplatePayload,
} from '~/connect/types';

import { ValidationErrorResponse } from '@wyz/types';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  useManageBrands,
  useManageCriteria,
  useManageSegments,
} from '~/common/api';
import { useValidationForm } from '~/common/hooks';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { CommonErrorCodesEnumType, TranslationKeys } from '~/common/types';
import { BrandTemplateHelpers } from '~/connect/helpers';
import { useScrollToNewItem } from '~/connect/hooks';

const DEFAULT_VALUES: IBrandTemplateForm = {
  name: '',
  criteria: [],
  brands: [],
  brandEanSpecification: BRAND_EAN_SPECIFICATIONS.NO_SPECIFICATION,
};

type Props =
  | {
      defaultValues?: Partial<IBrandTemplateForm>;
      mode?: 'CREATE' | 'UPDATE';
      isLoading: boolean;
      onSubmit: (
        data: IBrandTemplatePayload,
        formState: {
          criteria: ReturnType<UseFormGetFieldState<IBrandTemplatePayload>>;
        },
      ) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: Partial<IBrandTemplateForm>;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
    };

const Tabs = TabsFactory<BrandTemplateTabType>();

export function BrandTemplateForm({
  mode = 'CREATE',
  defaultValues = DEFAULT_VALUES,
  isLoading,
  onSubmit,
  errors,
}: Props) {
  const { t } = useI18n();

  const methods = useForm<IBrandTemplateForm>({
    defaultValues,
    resolver: zodResolver(brandTemplateFormSchema),
  });

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });

  const [searchQuery, setSearchQuery] = useState<string>('');

  const { fields, insert, remove, append, update } = useFieldArray<
    IBrandTemplateForm,
    'brands',
    'key'
  >({
    control: methods.control,
    name: 'brands',
    keyName: 'key',
  });

  const handleOnAdd = (brand: IBrandTemplateBrand) => {
    append(brand);
  };

  useScrollToNewItem(fields, 'brand', 'name');

  const handleOnRemove = (publicKey: string) => {
    const index = fields.findIndex((field) => field.publicKey === publicKey);
    remove(index);
  };

  const handleOnMove = useCallback(
    (from: number, to: number) => {
      const value = fields[from];
      remove(from);
      insert(to, value);
    },
    [fields, remove, insert],
  );

  const segments = useManageSegments({ list: { enabled: true } });

  const brands = useManageBrands({ list: { enabled: true } });
  const brandsByLetterByTab = useMemo(
    () =>
      BrandTemplateHelpers.groupByLetterByTab(
        brands.list.data?.data ?? [],
        segments.list.data?.data ?? [],
      ),
    [brands.list.data?.data, segments.list.data?.data],
  );

  const manageCriteria = useManageCriteria({ getAll: { enabled: true } });
  const [configuredBrand, setConfiguredBrand] = React.useState<
    IBrandTemplateForm['brands'][number] | undefined
  >();

  const configuredBrandIndex = configuredBrand
    ? fields.findIndex((field) => field.publicKey === configuredBrand.publicKey)
    : undefined;

  const onSelectGlobalEanSpecification = (
    value: IBrandTemplate['brandEanSpecification'],
  ) => {
    fields.forEach((field, index) => {
      if (!isNil(field.brandEanSpecification)) {
        methods.setValue(`brands.${index}.brandEanSpecification`, value);
      }
    });
  };

  const onSelectBrandEanSpecification = (
    value: IBrandTemplate['brandEanSpecification'],
  ) => {
    if (!isNil(configuredBrandIndex)) {
      update(configuredBrandIndex, {
        ...fields[configuredBrandIndex],
        brandEanSpecification: value,
      });
    }
  };

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) =>
        onSubmit?.(
          BrandTemplateHelpers.fromFormToPayload(
            data,
            manageCriteria.getAll.data?.data ?? [],
          ),
          { criteria: methods.getFieldState('criteria') },
        )
      }
      formConfiguration={{
        mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
      }}
    >
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='post' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.general_information' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs='3'>
              <TextField
                required
                name='name'
                label={<Trans i18nKey='app.common.model_name' />}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Flex justifyContent='between'>
            <Title heading='lg' color='tertiary' weight='bold'>
              <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
              <span className='ms-2'>
                <Trans i18nKey='app.common.criteria' />
              </span>
            </Title>
          </Flex>
        </Card.Header>
        <Card.Body>
          <CriteriaBuilder name={'criteria'}>
            <Row className='mt-4'>
              <Col md={6}>
                <MatchedCustomersTable
                  featureContext={FEATURE_CONTEXT.BRAND_TEMPLATE}
                  name={'criteria'}
                />
              </Col>
            </Row>
          </CriteriaBuilder>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header>
          <Card.Title>
            <Title heading='lg' color='tertiary' weight='bold'>
              <Icon icon='team' width='1.5rem' height='1.5rem' />
              <span className='ms-2'>
                <Trans i18nKey='app.brand_template.form.brands.title' />
              </span>
            </Title>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col
              xs='7'
              md={configuredBrand ? 2 : 6}
              className={clsx('animate-slide', {
                'opacity-25': configuredBrand,
              })}
            >
              <AlphabeticalListSearchInput
                value={searchQuery}
                label={<Trans i18nKey='app.brand_template.form.brands.label' />}
                placeholder={t(
                  'app.brand_template.list.table.search.placeholder',
                )}
                onChange={setSearchQuery}
              />
              <Tabs.Container defaultActiveKey='ALL'>
                <Tabs.List>
                  {BrandTemplateHelpers.TABS.map((tab) => (
                    <Tabs.Item
                      key={tab.value}
                      eventKey={tab.value}
                      id={tab.value}
                    >
                      <Trans
                        i18nKey={tab.label}
                        values={{
                          count: Object.values(
                            brandsByLetterByTab[tab.value],
                          ).reduce(
                            (total, brandsByLetter) =>
                              total + brandsByLetter.length,
                            0,
                          ),
                        }}
                      />
                    </Tabs.Item>
                  ))}
                </Tabs.List>

                <Tabs.Content>
                  {BrandTemplateHelpers.TABS.map((tab) => (
                    <Tabs.Pane key={tab.value} eventKey={tab.value}>
                      <BrandAlphabeticalList
                        currentTab={tab.value}
                        searchQuery={searchQuery}
                        onAdd={handleOnAdd}
                        onRemove={handleOnRemove}
                        itemsByTab={brandsByLetterByTab[tab.value]}
                      />
                    </Tabs.Pane>
                  ))}
                </Tabs.Content>
              </Tabs.Container>
            </Col>
            <Col
              xs='6'
              md={6}
              className='position-relative animate-slide'
              style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
            >
              {configuredBrand && (
                <div className='position-absolute' style={{ right: '105%' }}>
                  <IconButton
                    id='close-configuration'
                    icon='arrow-left-s-line'
                    variant='secondary'
                    onClick={() => setConfiguredBrand(undefined)}
                    rounded
                  />
                </div>
              )}
              {fields.length !== 0 ? (
                <>
                  <Title heading='lg' weight='bold'>
                    <Trans
                      i18nKey='app.brand_template.form.active_brands.title'
                      values={{
                        count: fields.length,
                      }}
                    />
                  </Title>
                  <div className='mb-4'>
                    <SelectBrandEanConfiguration
                      name='brandEanSpecification'
                      onSelect={onSelectGlobalEanSpecification}
                    />
                    <DesadvGlobalCheckbox
                      onSelectAll={() => {
                        fields.forEach((_, index) => {
                          methods.setValue(`brands.${index}.desadv`, true);
                        });
                      }}
                      onDeselectAll={() => {
                        fields.forEach((_, index) => {
                          methods.setValue(`brands.${index}.desadv`, false);
                        });
                      }}
                    />
                  </div>
                  <BrandDraggables
                    onRemove={handleOnRemove}
                    onMove={handleOnMove}
                    brands={fields}
                    onConfigure={setConfiguredBrand}
                  />
                </>
              ) : (
                <div className='text-center'>
                  <Text
                    heading='sm'
                    weight='bold'
                    color='tertiary'
                    className='mb-1'
                  >
                    <Trans i18nKey='app.brand_template.form.active_brands.empty.title' />
                  </Text>
                  <Text heading='sm' weight='normal' color='secondary-gray'>
                    <Trans i18nKey='app.brand_template.form.active_brands.empty.message' />
                  </Text>
                </div>
              )}
              {methods.formState.errors.brands?.message && (
                <FormFeedback invalid>
                  <Trans
                    i18nKey={
                      methods.formState.errors.brands
                        ?.message as keyof TranslationKeys
                    }
                  />
                </FormFeedback>
              )}
            </Col>
            {!isNil(configuredBrand) && !isNil(configuredBrandIndex) && (
              <Col
                key={configuredBrandIndex}
                md={4}
                className='animate-slide'
                style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
              >
                <Title heading='lg' weight='bold' wrap>
                  <Trans
                    i18nKey='app.brand_template.form.brands.configure_brand'
                    values={{
                      brandName: configuredBrand.name,
                    }}
                  />
                </Title>
                <SelectBrandEanConfiguration
                  name={`brands.${configuredBrandIndex}.brandEanSpecification`}
                  onSelect={onSelectBrandEanSpecification}
                />
                <Text heading='sm' weight='light' color='primary'>
                  <Trans i18nKey='app.brand_template.form.brands.desadv_enabled.title' />
                </Text>
                <CheckboxField
                  label={
                    <Trans i18nKey='app.brand_template.form.brands.desadv_enabled.label' />
                  }
                  name={`brands.${configuredBrandIndex}.desadv`}
                  formGroupClassName=''
                  asTextField={false}
                />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
      {mode !== 'READ_ONLY' && (
        <Card className='mb-section'>
          <Card.Body>
            <Flex className='space-x-4' justifyContent='between'>
              <Button
                id='cancel'
                as={Link}
                variant='secondary'
                type='button'
                to='/platform/distribution/products-brands'
              >
                <Trans i18nKey='app.common.button.cancel' />
              </Button>
              <Button
                id='submit'
                variant='primary'
                type='submit'
                disabled={isLoading}
              >
                <Trans
                  i18nKey={
                    mode === 'CREATE'
                      ? 'app.brand_template.form.buttons.create'
                      : 'app.brand_template.form.buttons.update'
                  }
                />
              </Button>
            </Flex>
          </Card.Body>
        </Card>
      )}
    </FormContainer>
  );
}
